import React from "react";
import { ReactTFC } from "../../../utils/fp/missing-ts-parts";

import { css } from "@emotion/react";

import { ProductVariantPickerLayoutComponent } from "./pickers/ProductVariantPickerLayoutComponent";

import { EnrichedSwarovskiProductFrontendProductAggregatedReviews, type RelatedBundleElemT } from "@swo/storefront";
import { useCtxFns, usePageLocaleContext } from "../../../global/page-context";
import { CSS_MQ } from "../../../styles/breakpoints";
import { PDPGalleryModalRoot } from "../PDPGalleryModalRoot";
import { PDPScrollingGallery } from "../PDP_Gallery/PDPScrollingGallery";
import { PdpGalleryWithLightboxProps } from "../PDP_Lightbox/use-pdp-gallery-with-lightbox";
import { ReviewTranslations } from "../reviewTranslations";
import { PDPAboveTheFoldLayoutWrapper } from "./PDPAboveTheFoldLayoutWrapper";
import { PDPDetailsWrapper } from "./PDPDetailsWrapper";
import { PDPImageWithPlaceholder } from "./PDPImageWithPlaceholder";
import { PDPReviewSummary } from "./PDPReviewSummary";
import { ProductDetailsIntro } from "./ProductDetailsIntro";
import { REVIEWS_SECTION_ID } from "./REVIEWS_SECTION_ID";
import { PdpAvailabilityState } from "./availability/types";
import { PDPState } from "./availability/use-product-variant-state";
import { PDPGiftCardCustomisation } from "./gift-card/PDPGiftCardCustomisation";
import { PDPGiftCardProps } from "./gift-card/usePDPGiftCardProps";
import { PDPStickyPriceBar } from "./price-bar/PDPStickyPriceBar";
import { getPDPStockLevelIndicator2 } from "./price-bar/PDPStockLevelIndicator";
import { PDPAction } from "./state/types";
import { PDPDataProps } from "./types";
import { useLocaleLanguageEnglishName } from "./use-locale-language";
import { PDPBundleTeaser } from "./PDPBundleTeaser";

type PDPLayoutProps = Pick<PDPDataProps, "taxonomy"> & {
  pdpState: PDPState;
  dispatch: React.Dispatch<PDPAction>;
  DescriptionComponent: React.ReactNode;
  galleryWithLightbox: PdpGalleryWithLightboxProps;
  giftCardProps: PDPGiftCardProps | undefined;
  availabilityState: PdpAvailabilityState;
  primaryButton: React.ReactNode;
  reviewsT: ReviewTranslations;
  reviews: EnrichedSwarovskiProductFrontendProductAggregatedReviews;
  PdpInformationSet: React.ReactNode;
  relatedBundles: Array<RelatedBundleElemT>;
};

export const PDPAboveTheFoldLayout: ReactTFC<PDPLayoutProps> = ({
  reviewsT,
  pdpState,
  giftCardProps,
  galleryWithLightbox,
  availabilityState,
  dispatch,
  primaryButton,
  taxonomy,
  DescriptionComponent,
  reviews,
  PdpInformationSet,
  relatedBundles,
}) => {
  const { locale } = usePageLocaleContext();
  const { showPrice } = useCtxFns();

  const localeLanguageEnglishName = useLocaleLanguageEnglishName(locale);

  return (
    <PDPAboveTheFoldLayoutWrapper>
      <PDPImageWithPlaceholder
        galleryWithLightbox={galleryWithLightbox}
        availability={pdpState.computed.availability}
      />

      <PDPDetailsWrapper
        Element={
          <PDPStickyPriceBar
            primaryButton={primaryButton}
            extension={availabilityState.extension}
            priceLabel={showPrice(pdpState.selectedVariant.price)}
            stockLevel={getPDPStockLevelIndicator2(pdpState.computed.availability)}
          />
        }
      >
        <ProductDetailsIntro
          breadcrumbs={taxonomy.breadcrumbs}
          isNew={pdpState.selectedProduct.isNew}
          name={pdpState.selectedProduct.name}
          SubheadlineElement={
            reviews ? (
              <PDPReviewSummary reviewsData={reviews} locale={locale} id={REVIEWS_SECTION_ID} t={reviewsT} />
            ) : null
          }
        />

        {DescriptionComponent}

        <ProductVariantPickerLayoutComponent
          localeLanguageEnglishName={localeLanguageEnglishName}
          pdpState={pdpState}
          dispatch={dispatch}
        />

        {giftCardProps && <PDPGiftCardCustomisation {...giftCardProps} />}
      </PDPDetailsWrapper>

      <PDPProductDetailsIntro>
        {PdpInformationSet}
        <PDPBundleTeaser bundles={relatedBundles} />
        <PDPScrollingGallery galleryWithLightbox={galleryWithLightbox} />
      </PDPProductDetailsIntro>

      <PDPGalleryModalRoot galleryWithLightbox={galleryWithLightbox} />
    </PDPAboveTheFoldLayoutWrapper>
  );
};

export const PDPProductDetailsIntro: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div
      className="js-product-details-intro"
      css={css`
        ${CSS_MQ.atLeastMd} {
          grid-column: 2;
        }
      `}
    >
      {children}
    </div>
  );
};
